import { EegDataRecord } from "../types";

type SortableHeader = {
    name: string;
    order: number;
};

type SortableRow = {
    name: string;
    order: number;
};


export const extractRowKeyName = (record: EegDataRecord): string | null => {
    if (record.single_electrode_location) {
        const loc = record.single_electrode_location;
        if (loc.name || loc.name_external) {
            return loc.name || loc.name_external || "Unknown";
        }
    }
    if (record.pair_electrode_location) {
        const pair = record.pair_electrode_location;
        const one = pair.electrode_location_one;
        const two = pair.electrode_location_two;
        if (one && two && (one.name || one.name_external) && (two.name || two.name_external)) {
            return `${one.name || one.name_external} - ${two.name || two.name_external}`;
        }
    }
    return null;
};

export const createSortedHeaders = (records: Array<EegDataRecord>): Array<string> => {
    const headersMap = new Map<string, SortableHeader>();
    const allHeaders = new Set<string>();

    records.forEach((record) => {
        if (record.frequency_band?.name) {
            if (!headersMap.has(record.frequency_band.name)) {
                headersMap.set(record.frequency_band.name, {
                    name: record.frequency_band.name,
                    order: record.frequency_band.min_frequency,
                });
            }
            allHeaders.add(record.frequency_band.name);
        }
        const individualValue = record.frequency_individual_value;
        if (individualValue) {
            const name = individualValue.name_internal || individualValue.name_external;
            if (name && !headersMap.has(name)) {
                headersMap.set(name, {
                    name: name,
                    order: individualValue.frequency,
                });
            }
            if (name) {
                allHeaders.add(name);
            }
        }
        const pairFreqBands = record.pair_frequency_bands;
        if (pairFreqBands && typeof pairFreqBands === "object") {
            const bandOne = pairFreqBands.frequency_band_one;
            const bandTwo = pairFreqBands.frequency_band_two;
            if (bandOne && bandOne.name && bandTwo && bandTwo.name) {
                const combinedName = `${bandOne.name} / ${bandTwo.name}`;
                allHeaders.add(combinedName);
            }
        }
    });

    const sortedHeaders = Array.from(headersMap.values())
        .sort((a, b) => a.order - b.order)
        .map((h) => h.name);

    // Ensure all collected headers are included, even if they don't have sorting info
    allHeaders.forEach((header) => {
        if (!sortedHeaders.includes(header)) {
            sortedHeaders.push(header);
        }
    });

    return sortedHeaders;
};

export const handleElectrodeLocations = (record: EegDataRecord): SortableRow | null => {
    const rowName = extractRowKeyName(record);
    if (rowName === null) {
        return null;
    }
    if (record.single_electrode_location && record.single_electrode_location.order !== undefined) {
        return {
            name: rowName,
            order: record.single_electrode_location.order,
        };
    }
    if (record.pair_electrode_location && record.pair_electrode_location.electrode_location_one && record.pair_electrode_location.electrode_location_one.order !== undefined) {
        return {
            name: rowName,
            order: record.pair_electrode_location.electrode_location_one.order,
        };
    }
    return null;
};

export const sortRowKeys = (records: Array<EegDataRecord>): Array<string> => {
    const rowsMap = new Map<string, SortableRow>();

    records.forEach((record) => {
        const rowInfo = handleElectrodeLocations(record);
        if (rowInfo) {
            if (!rowsMap.has(rowInfo.name)) {
                rowsMap.set(rowInfo.name, rowInfo);
            }
        }
    });

    const sortedRows = Array.from(rowsMap.values())
        .sort((a, b) => a.order - b.order)
        .map((r) => r.name);

    return sortedRows;
};
